import styled from 'styled-components'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Typography } from 'components/Typography/Typography'
import { Stylable } from 'utils/types'
import { LpVaultsList } from './LpVaultsList'
import { Direction, SortBy, LpVaultListRow, TableValue } from '../types'
import { PAIR_INFOS, PairInfo } from 'utils/pair-info'
import { useGlobalPositionsInfo } from '@/shared/kai/hooks/useGlobalInfo'
import { DONT_SHOW_THIS_AGAIN_LP_LS_KEY } from 'modules/LpVaultDetails/LpVaultDetails'
import { WhatIsSLPVault } from './WhatIsLpVault'
import { LpVaultExplanation } from 'modules/LpVaultDetails/LpVaultExplanation'
import { SearchInput } from 'components/SearchInput/SearchInput'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'

interface Props extends Stylable {
  isHomePage?: boolean
  searchQuery?: string
}

const VaultsWrapper = styled.div`
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding: 0px 20px;
  }
`

const VaultsContainer = styled.div`
  max-width: 1200px;
  margin: auto;
  position: relative;
`

const VaultsSearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
    margin-top: 80px;
    margin-bottom: 0;

    & > div {
      font-size: ${p => p.theme.typography.size.Subtitle2}px;
      margin-bottom: 16px;
    }
  }
`

const getNameFieldForSort = (sortKey: SortBy) => {
  switch (sortKey) {
    case SortBy.Apr:
      return TableValue.Apr
    case SortBy.Deposited:
      return TableValue.Deposit
    case SortBy.Tvl:
      return TableValue.Tvl
    default:
      return TableValue.Deposit
  }
}

const getInitialStateData = (lpDisplayInfoList: PairInfo<string>[]): LpVaultListRow[] => {
  return lpDisplayInfoList.map(v => ({
    ...v,
    [SortBy.Deposited]: undefined,
    [SortBy.Earned]: undefined,
    [SortBy.Apr]: undefined,
    [SortBy.Tvl]: undefined,
    tvl: undefined,
    deposited: undefined,
    apr: v.initialAprValue,
    earned: undefined,
  }))
}

export const LpVaultsListContainer = ({ style, isHomePage, searchQuery }: Props) => {
  const walletAddress = useCurrentWalletAddress()

  const { data: globalPositionInfoData, isLoading: globalPositionsInfoLoading } = useGlobalPositionsInfo(walletAddress)

  const [isDontShowSelected, setIsDontShowSelected] = useState(
    localStorage.getItem(DONT_SHOW_THIS_AGAIN_LP_LS_KEY) === 'true'
  )

  const setDontShowThisAgain = useCallback(() => {
    setIsDontShowSelected(true)
    localStorage.setItem(DONT_SHOW_THIS_AGAIN_LP_LS_KEY, 'true')
  }, [])

  const resetDontShowThisAgain = useCallback(() => {
    setIsDontShowSelected(false)
    localStorage.setItem(DONT_SHOW_THIS_AGAIN_LP_LS_KEY, 'false')
  }, [])

  const [searchText, setSearchText] = useState(searchQuery || '')
  const [sortConfig, setSortConfig] = useState<{ key: SortBy | null; direction: Direction | null }>({
    key: null,
    direction: null,
  })

  useEffect(() => {
    setSearchText(searchQuery || '')
  }, [searchQuery])

  const displayRows = useMemo(() => {
    const initialRows = getInitialStateData(PAIR_INFOS)
    if (!globalPositionInfoData) {
      return initialRows
    }
    return [...initialRows]
      .map(r => {
        return {
          ...r,
          tvl: r.configInfos.reduce((sum, info) => sum + (globalPositionInfoData.tvlByConfigId[info.configId] || 0), 0),
          deposited: r.configInfos.reduce(
            (sum, info) => sum + (globalPositionInfoData.walletDepositedByConfigIdUsd[info.configId] || 0),
            0
          ),
          earned: r.configInfos.reduce(
            (sum, info) => sum + (globalPositionInfoData.walletEarnedByConfigIdUsd[info.configId] || 0),
            0
          ),
        }
      })
      .sort((a, b) => (b.tvl ?? 0) - (a.tvl ?? 0))
  }, [globalPositionInfoData])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }, [])

  const handleClear = useCallback(() => {
    setSearchText('')
  }, [])

  const handleColumnHeaderClick = useCallback(
    (key: SortBy) => {
      let direction = Direction.Descending
      if (sortConfig.key !== SortBy.Vault && sortConfig.key === key && sortConfig.direction === Direction.Descending) {
        direction = Direction.Ascending
      }
      setSortConfig({ key, direction })
    },
    [sortConfig]
  )

  const displayData = useMemo(() => {
    const displayLpVaults = displayRows.filter(
      v =>
        v.coinX.displaySymbol.toLowerCase().indexOf(searchText.toLowerCase()) !== -1 ||
        v.coinY.displaySymbol.toLowerCase().indexOf(searchText.toLowerCase()) !== -1
    )

    if (!sortConfig.key) {
      return displayLpVaults
    }

    const sortableItems = [...displayLpVaults]

    switch (sortConfig.key) {
      case SortBy.Vault:
        // Get initial hardcoded order
        // eslint-disable-next-line no-case-declarations
        const newItems = []
        for (const v of PAIR_INFOS) {
          const foundItem = displayLpVaults.find(
            i => i.coinX.displaySymbol === v.coinX.displaySymbol && i.coinY.displaySymbol === v.coinY.displaySymbol
          )
          if (foundItem) {
            newItems.push(foundItem)
          }
        }
        return newItems
      case SortBy.Apr:
      case SortBy.Tvl:
      case SortBy.Deposited:
      case SortBy.Earned:
        return sortableItems.sort((a, b) => {
          const key = sortConfig.key
          if (key === null) {
            return 0
          }

          const fieldName = getNameFieldForSort(key)

          if (typeof a[fieldName] !== 'number' || typeof b[fieldName] !== 'number') {
            return 0
          }
          if (a[fieldName] < b[fieldName]) {
            return sortConfig.direction === Direction.Ascending ? -1 : 1
          }
          if (a[fieldName] > b[fieldName]) {
            return sortConfig.direction === Direction.Ascending ? 1 : -1
          }
          return 0
        })
      default:
        return displayLpVaults
    }
  }, [displayRows, sortConfig, searchText])

  return (
    <VaultsWrapper style={style}>
      <VaultsContainer>
        {!isHomePage && (
          <>
            <WhatIsSLPVault isDontShowSelected={isDontShowSelected} onResetDontShowThisAgain={resetDontShowThisAgain} />
            <LpVaultExplanation
              style={{ margin: '32px 0' }}
              isDontShowSelected={isDontShowSelected}
              onDontShowThisAgainClick={setDontShowThisAgain}
            />
          </>
        )}
        {!isHomePage && (
          <VaultsSearchContainer>
            <Typography variant="title2">
              All <span style={{ fontWeight: 700 }}>LP Vaults</span>
            </Typography>
            <SearchInput text={searchText} onChange={handleInputChange} onClear={handleClear} />
          </VaultsSearchContainer>
        )}
        <LpVaultsList
          displayData={displayData}
          sortConfig={sortConfig}
          onColumnHeaderClick={handleColumnHeaderClick}
          isLoading={globalPositionsInfoLoading}
        />
      </VaultsContainer>
    </VaultsWrapper>
  )
}
